import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const DESKTOP_NEWLINE_PATTERN = /<desktopBrowserNewline\s*\/?>/gi;
const DESKTOP_NEWLINE_CLOSE_PATTERN = /<\/desktopBrowserNewline>/gi;

export interface TypewriterScriptPayload {
  script: string;
  updatedAt?: string;
  versionNotes?: string;
}

/**
 * Service for retrieving the active typewriter auto-typing script from Firestore.
 * Scripts live in the `system/typewriterScripts` document with a `script` field and optional history.
 */
export class TypewriterScriptService {
  private static cache: string | null = null;
  private static lastFetch: number | null = null;
  private static readonly CACHE_TTL_MS = 60_000; // 1 minute cache

  /**
   * Fetches the current script text. Falls back to empty string if the document
   * or field is missing.
   */
  static async getCurrentScript(): Promise<string> {
    const now = Date.now();
    if (this.cache && this.lastFetch && now - this.lastFetch < this.CACHE_TTL_MS) {
      return this.cache;
    }

    try {
      // Try paths in order of preference:
      // 1. system/typewriterScripts (document with script field)
      // 2. system/typeWriterScript/Script (alternative path)
      let snapshot = await getDoc(doc(db, 'system', 'typewriterScripts'));
      
      if (!snapshot.exists()) {
        snapshot = await getDoc(doc(db, 'system', 'typeWriterScript', 'Script'));
      }
      
      console.log('📜 Typewriter script fetch:', {
        exists: snapshot.exists(),
        hasScript: !!snapshot.data()?.script,
        scriptLength: snapshot.data()?.script?.length || 0,
      });

      if (!snapshot.exists()) {
        console.warn('⚠️ typewriter script document not found in Firestore (tried both paths)');
        this.cache = '';
        this.lastFetch = now;
        return this.cache;
      }

      const data = snapshot.data() as TypewriterScriptPayload;
      const rawScript = data?.script ?? '';
      const normalized = this.normalizeScript(rawScript);

      this.cache = normalized;
      this.lastFetch = now;
      return normalized;
    } catch (error) {
      console.error('❌ Failed to load typewriter auto script from Firestore:', error);
      throw error;
    }
  }

  /**
   * Clears the cached script so the next call will re-fetch.
   */
  static invalidateCache() {
    this.cache = null;
    this.lastFetch = null;
  }

  /**
   * Normalizes script text by translating helper tags into newline characters.
   * Supports either <desktopBrowserNewline /> or paired open/close tags.
   */
  private static normalizeScript(script: string): string {
    return script
      .replace(DESKTOP_NEWLINE_PATTERN, '\n')
      .replace(DESKTOP_NEWLINE_CLOSE_PATTERN, '');
  }
}


